import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Instagram, Music2, MessageCircle, Twitter, Facebook, ImageDown, Copy, Link as LinkIcon, Share2, Check } from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/track";
import { canonicalShareUrl, rewriteCaptionUrls } from "../lib/shareUrl";

const TARGETS = [
    { key: "link", label: "Copy Link", icon: LinkIcon, hint: "URL" },
    { key: "instagram", label: "Instagram Story", icon: Instagram, hint: "IG" },
    { key: "tiktok", label: "TikTok Slides", icon: Music2, hint: "TT" },
    { key: "whatsapp", label: "WhatsApp Status", icon: MessageCircle, hint: "WA" },
    { key: "twitter", label: "X / Twitter", icon: Twitter, hint: "X" },
    { key: "facebook", label: "Facebook Story", icon: Facebook, hint: "FB" },
    { key: "save", label: "Save to Photos", icon: ImageDown, hint: "PNG" },
    { key: "caption", label: "Copy Caption", icon: Copy, hint: "TXT" },
];

const buildDeepLink = (target, caption, shareUrl) => {
    // For Twitter/FB the URL is passed separately so they auto-link; do not double-include it.
    // For WhatsApp the URL must be inline because previews follow the text.
    const cleanText = (caption || "").replace(shareUrl || "_____never_match_____", "").trim();
    const text = encodeURIComponent(cleanText || caption || "");
    const fullText = encodeURIComponent(caption || "");
    const url = encodeURIComponent(shareUrl || (typeof window !== "undefined" ? window.location.href : ""));
    switch (target) {
        case "twitter":
            // X auto-cards from URL — pass the trimmed text + URL separately
            return `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
        case "facebook":
            return `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`;
        case "whatsapp":
            // WhatsApp: full caption (with embedded URL) — guarantees the link
            // renders as a preview chip below the text.
            return `https://wa.me/?text=${fullText}`;
        default:
            return null;
    }
};

export const ShareSheet = ({ open, onOpenChange, caption, captionNoUrl, exportPng, onCopyCaption, onDownload, shareUrl, privateMessage, privateCapsule, privateTone, defaultRecipient = "" }) => {
    // Tone toggle — "private" reads emotional ("Someone left you a MoodCard.
    // Open gently: <link>"), "public" reads viral ("This is the line you'd
    // post — see it best → <link> #MoodCard"). Defaults to private when a
    // privateMessage is supplied (i.e. when the caller built one).
    const [tone, setTone] = useState(privateMessage ? "private" : "public");
    const [active, setActive] = useState(null);

    // Recipient name — optional. When the sender types a name, the share URL
    // gets `?to=<name>` appended so the recipient's sleeve intro reads
    // "for Mira — open gently". Hand-addressed feel = ~30-50% SMS open lift
    // per benchmark. Local state, sanitized at use time. When this card is
    // being created as a REPLY (user clicked "send back to Adam" upstream),
    // defaultRecipient seeds the field so they don't have to retype.
    const [recipientName, setRecipientName] = useState(defaultRecipient || "");
    const cleanRecipient = recipientName
        .replace(/[^a-zA-Z \-']/g, "")
        .trim()
        .slice(0, 24);
    // ── Canonical share URL ──
    // Force the host to moodcard.app before ANY downstream use. Belt &
    // suspenders against backend config regressions that ever embedded
    // a preview-environment URL in `shareUrl`. Recipients must ALWAYS
    // land on production, never on a preview/dev URL where the
    // "Wake up servers" banner would appear.
    const safeShareUrl = canonicalShareUrl(shareUrl);
    const effectiveShareUrl = safeShareUrl
        ? (cleanRecipient
            ? `${safeShareUrl}${safeShareUrl.includes("?") ? "&" : "?"}to=${encodeURIComponent(cleanRecipient)}`
            : safeShareUrl)
        : safeShareUrl;

    // Sanitize captions too — these often embed the share URL verbatim
    // ("view in full → https://...") and would otherwise leak the
    // preview domain in copy-pasted text.
    const safeCaption = rewriteCaptionUrls(caption);
    const safeCaptionNoUrl = captionNoUrl;
    const safePrivateMessage = rewriteCaptionUrls(privateMessage);

    // If a recipient is set, swap the URL inside privateMessage so the link
    // we COPY and the link we SHARE match the preview.
    const effectivePrivateMessage = (safePrivateMessage && safeShareUrl && effectiveShareUrl !== safeShareUrl)
        ? safePrivateMessage.split(safeShareUrl).join(effectiveShareUrl)
        : safePrivateMessage;

    // Resolved caption pair based on selected tone.
    const effectiveCaption = tone === "private" && effectivePrivateMessage ? effectivePrivateMessage : safeCaption;
    const effectiveCaptionNoUrl = tone === "private" && effectivePrivateMessage
        ? (effectivePrivateMessage || "").replace(effectiveShareUrl || "_____never_____", "").trim()
        : safeCaptionNoUrl;

    const dataUrlToBlob = async (dataUrl) => {
        const res = await fetch(dataUrl);
        return await res.blob();
    };

    const handleTarget = async (key) => {
        setActive(key);
        track("share_target_click", { target: key, with_recipient: !!cleanRecipient });

        try {
            if (key === "link") {
                const url = effectiveShareUrl || (typeof window !== "undefined" ? window.location.href : "");
                try {
                    await navigator.clipboard.writeText(url);
                    track("copy_link_clicked", { source: "share_sheet", with_recipient: !!cleanRecipient });
                    toast.success(cleanRecipient ? `Link copied — addressed to ${cleanRecipient}` : "Link copied");
                } catch (e) {
                    console.error(e);
                }
                return;
            }
            if (key === "caption") {
                // Copy whatever caption the user has SELECTED (private vs viral).
                if (tone === "private" && effectivePrivateMessage) {
                    try { await navigator.clipboard.writeText(effectivePrivateMessage); toast.success("Caption copied"); } catch { /* ignore */ }
                } else {
                    await onCopyCaption();
                }
                return;
            }
            if (key === "save") {
                await onDownload();
                return;
            }

            // TikTok — no web→app share API exists. Best-effort flow:
            //   1. Download the 9:16 PNG so the user has the asset
            //   2. Copy the caption to clipboard so they just paste
            //   3. Try opening the TikTok app via deep-link (mobile),
            //      else open the TikTok upload page (desktop)
            if (key === "tiktok") {
                try { await navigator.clipboard.writeText(effectiveCaption || ""); } catch { /* ignore */ }
                await onDownload();
                track("share_tiktok_handoff", {});
                const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
                if (isMobile) {
                    // Try the TikTok app universal link. iOS auto-opens app if installed;
                    // Android needs the intent fallback to upload page.
                    toast.success("Card saved · caption copied · opening TikTok…");
                    // Universal link form — opens app on iOS / Play Store fallback on Android
                    window.location.href = "https://www.tiktok.com/upload";
                } else {
                    toast.success("Card saved · caption copied. Upload it on TikTok ↗");
                    window.open("https://www.tiktok.com/upload", "_blank", "noopener,noreferrer");
                }
                return;
            }

            // Try Web Share API with image first (best on mobile)
            if (navigator.share) {
                try {
                    const dataUrl = await exportPng();
                    const blob = await dataUrlToBlob(dataUrl);
                    const file = new File([blob], `moodcard.png`, { type: "image/png" });

                    // For navigator.share: pass `text` WITHOUT the URL (the platform appends `url` itself,
                    // otherwise the URL gets duplicated in WhatsApp / IG.)
                    const cleanText = effectiveCaptionNoUrl || (effectiveCaption || "").replace(safeShareUrl || "_____never_____", "").trim();

                    if (navigator.canShare && navigator.canShare({ files: [file] })) {
                        await navigator.share({
                            files: [file],
                            text: cleanText,
                            title: "MoodCard",
                            url: effectiveShareUrl,
                        });
                        return;
                    }
                    await navigator.share({ text: cleanText, title: "MoodCard", url: effectiveShareUrl });
                    return;
                } catch (err) {
                    console.warn("web share failed", err);
                }
            }

            // Desktop / fallback — open web intent + download image
            const link = buildDeepLink(key, effectiveCaption, effectiveShareUrl);
            if (link) window.open(link, "_blank", "noopener,noreferrer");

            if (key === "instagram" || key === "tiktok" || key === "whatsapp" || key === "facebook") {
                await onDownload();
            }
        } finally {
            setTimeout(() => setActive(null), 1400);
            // The reply loop has closed — clear the seed so the next
            // unrelated card doesn't accidentally inherit this recipient.
            if (cleanRecipient) {
                try {
                    window.sessionStorage.removeItem("moodcard.reply_to");
                    window.sessionStorage.removeItem("moodcard.reply_from_slug");
                } catch { /* ignore */ }
            }
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={() => onOpenChange(false)}
                        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
                        data-testid="share-sheet-backdrop"
                    />
                    <motion.div
                        initial={{ y: "100%" }}
                        animate={{ y: 0 }}
                        exit={{ y: "100%" }}
                        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed bottom-0 left-0 right-0 z-50 max-w-md mx-auto"
                        data-testid="share-sheet"
                    >
                        <div className="relative bg-app-2 border-t border-x border-app/30 rounded-t-3xl shadow-[0_-20px_60px_rgba(0,0,0,0.6)] overflow-hidden">
                            <div className="absolute inset-0 grain pointer-events-none" />
                            <div className="relative px-6 pt-5 pb-8">
                                <div className="mx-auto w-10 h-1 rounded-full bg-surface-3 mb-6" />

                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-app-3">
                                            share to
                                        </span>
                                        <h3 className="mt-1 font-serif font-light text-app text-2xl tracking-tight flex items-center gap-2">
                                            <Share2 className="w-4 h-4 text-app-2" strokeWidth={1.5} />
                                            your card
                                        </h3>
                                    </div>
                                    <button
                                        onClick={() => onOpenChange(false)}
                                        className="p-2 rounded-full hover:bg-surface-2 transition-colors text-app-2 hover:text-app"
                                        data-testid="share-sheet-close"
                                    >
                                        <X className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                </div>

                                {/* Private ↔ Public tone toggle. Lets the user
                                    decide if this share reads like "someone left
                                    you a feeling" (private) or "here's the line
                                    you'd post" (viral). */}
                                {privateMessage && (
                                    <div className="mb-5">
                                        <div className="flex items-center gap-1 p-1 rounded-full border border-app/25 bg-surface-1 w-fit" data-testid="share-tone-toggle">
                                            <button
                                                onClick={() => setTone("private")}
                                                data-testid="share-tone-private"
                                                className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.2em] transition-colors ${
                                                    tone === "private"
                                                        ? "bg-cta text-app-inverse"
                                                        : "text-app-3 hover:text-app"
                                                }`}
                                            >
                                                send quietly
                                            </button>
                                            <button
                                                onClick={() => setTone("public")}
                                                data-testid="share-tone-public"
                                                className={`px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.2em] transition-colors ${
                                                    tone === "public"
                                                        ? "bg-cta text-app-inverse"
                                                        : "text-app-3 hover:text-app"
                                                }`}
                                            >
                                                post publicly
                                            </button>
                                        </div>

                                        {/* "for someone" — appears only in private tone. Hand-addresses
                                            the share so the recipient's sleeve intro reads
                                            "for Mira · a feeling has arrived". Optional & local. */}
                                        {tone === "private" && (
                                            <div className="mt-3 flex items-center gap-2" data-testid="share-recipient-row">
                                                <span className="font-sans text-[9px] uppercase tracking-[0.35em] text-app-3 shrink-0">
                                                    for
                                                </span>
                                                <input
                                                    type="text"
                                                    value={recipientName}
                                                    onChange={(e) => setRecipientName(e.target.value)}
                                                    placeholder="(optional name)"
                                                    maxLength={24}
                                                    data-testid="share-recipient-input"
                                                    className="flex-1 bg-transparent border-b border-app/20 focus:border-amber-200/50 outline-none font-serif italic text-[14px] text-app placeholder:text-app-3 placeholder:italic py-1 transition-colors"
                                                />
                                                {cleanRecipient && (
                                                    <button
                                                        onClick={() => setRecipientName("")}
                                                        data-testid="share-recipient-clear"
                                                        className="font-sans text-[9px] uppercase tracking-[0.3em] text-app-3 hover:text-app"
                                                    >
                                                        clear
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        <div className="mt-3 px-3.5 py-3 rounded-xl border border-app/15 bg-surface-1/60" data-testid="share-preview-pane">
                                            {tone === "private" && cleanRecipient && (
                                                <div className="mb-2 flex items-baseline gap-1.5" data-testid="share-preview-recipient">
                                                    <span className="font-sans text-[9px] uppercase tracking-[0.4em] text-amber-200/60">
                                                        for
                                                    </span>
                                                    <span className="font-serif italic text-[15px] text-amber-100/95">
                                                        {cleanRecipient}
                                                    </span>
                                                </div>
                                            )}
                                            {tone === "private" && privateCapsule && (
                                                <div className="mb-1.5 font-sans text-[9px] font-semibold uppercase tracking-[0.32em] text-amber-200/80">
                                                    {privateCapsule}
                                                </div>
                                            )}
                                            {(() => {
                                                // Render the preview as: bold title (first sentence) + softer body.
                                                // First non-empty line acts as the title — uppercase and weighted.
                                                // Rest cascades as italic body. URL on its own line shows muted.
                                                const lines = (effectiveCaption || "").split("\n").filter(Boolean);
                                                const titleLine = lines[0] || "";
                                                const bodyLines = lines.slice(1);
                                                return (
                                                    <>
                                                        <p className="font-sans text-[12px] font-semibold uppercase tracking-[0.14em] text-app leading-snug">
                                                            {titleLine}
                                                        </p>
                                                        {bodyLines.length > 0 && (
                                                            <p className="mt-1.5 font-serif italic text-[13px] text-app-2 leading-snug whitespace-pre-line line-clamp-4">
                                                                {bodyLines.join("\n")}
                                                            </p>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                            {tone === "private" && privateTone && (
                                                <span className="mt-2 inline-block font-sans text-[9px] uppercase tracking-[0.28em] text-app-3">
                                                    tone · {privateTone}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-2.5">
                                    {TARGETS.map(({ key, label, icon: Icon, hint }) => (
                                        <button
                                            key={key}
                                            onClick={() => handleTarget(key)}
                                            data-testid={`share-${key}`}
                                            className="group flex items-center gap-3 p-4 rounded-xl border border-app/20 bg-surface-1 hover:bg-surface-2 hover:border-app/40 transition-all text-left"
                                        >
                                            <div className="h-9 w-9 flex items-center justify-center rounded-full bg-surface-2 border border-app/30">
                                                {active === key ? (
                                                    <Check className="w-4 h-4 text-app" strokeWidth={2} />
                                                ) : (
                                                    <Icon className="w-4 h-4 text-app" strokeWidth={1.5} />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-sans text-sm text-app truncate">{label}</div>
                                                <div className="font-sans text-[10px] uppercase tracking-[0.3em] text-app-3">
                                                    {hint}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>

                                <p className="mt-6 text-center font-serif italic text-[11px] text-app-3">
                                    your card has been saved with a soft watermark.
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};
